import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiSend,
  FiMessageCircle,
  FiClock,
  FiChevronDown,
  FiChevronUp
} from 'react-icons/fi';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { useTranslation } from '../hooks/useTranslation';
import { faqs } from '../data/mockData';
import './Contact.css';

const Contact = () => {
  const hydrated = useHydrated();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [openFaq, setOpenFaq] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // API call would go here
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us - Get in Touch"
        description="Have questions about astrology services? Contact AstroEpanchang via our contact form or live chat. Support available for all your astrology needs."
        keywords="contact astrologer, astrology support, astrology help, astroepanchang contact"
        canonicalPath="/contact"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact AstroEpanchang",
          "description": "Get in touch with our support team"
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Are AstroEpanchang's services completely free?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, our core services — Kundli generation, daily horoscopes, marriage compatibility check, Panchang, festival calendar, and muhurtham dates — are completely free to use. No registration is required for basic features. Premium personalized consultation services with certified astrologers are available at affordable rates."
              }
            },
            {
              "@type": "Question",
              "name": "How accurate are AstroEpanchang's calculations?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We use Swiss Ephemeris — the gold standard in astronomical calculations used by professional astrologers worldwide — with Lahiri Ayanamsa (the official ayanamsa of the Indian government). Our Panchang calculations are verified against traditional almanacs for maximum accuracy. Planetary positions are accurate to within seconds of arc."
              }
            },
            {
              "@type": "Question",
              "name": "How quickly will I receive a response to my inquiry?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We aim to respond to all general inquiries within 24-48 hours. Technical support requests are typically addressed within 12 hours during business hours. Premium members have access to priority support with faster response times."
              }
            },
            {
              "@type": "Question",
              "name": "Can I get a personalized astrological consultation?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, AstroEpanchang connects you with experienced, certified Vedic astrologers for personalized consultations on birth chart reading, marriage compatibility, career astrology, Muhurtham selection, and remedial measures (Parihara). Visit our Consultation page to browse available astrologers and book a session."
              }
            }
          ]
        }]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>{t('contact.title')}</h1>
          <p>{t('contact.subtitle')}</p>
        </motion.div>

        <div className="contact-grid">
          {/* Contact Form */}
          <motion.div
            className="contact-form-section"
            initial={hydrated ? { opacity: 0, x: -30 } : false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="form-card">
              <h3>{t('contact.sendMessage')}</h3>
              {submitted && (
                <div className="success-message">
                  {t('contact.thankYou')}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{t('contact.fullName')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('contact.namePlaceholder')}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">{t('contact.email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact.emailPlaceholder')}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">{t('contact.subject')}</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('contact.selectTopic')}</option>
                    <option value="general">{t('contact.generalInquiry')}</option>
                    <option value="technical">{t('contact.technicalSupport')}</option>
                    <option value="billing">{t('contact.billingIssue')}</option>
                    <option value="feedback">{t('contact.feedback')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contact.message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.messagePlaceholder')}
                    rows="5"
                    required
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary btn-full">
                  <FiSend /> {t('contact.send')}
                </button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            className="contact-info-section"
            initial={hydrated ? { opacity: 0, x: 30 } : false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="info-cards">
              <div className="info-card">
                <div className="info-icon">
                  <FiMessageCircle />
                </div>
                <h4>{t('contact.liveChat')}</h4>
                <p>{t('contact.liveChatDesc')}</p>
                <button className="btn btn-secondary btn-sm">{t('contact.startChat')}</button>
              </div>
            </div>

            <div className="support-hours">
              <FiClock /> {t('contact.supportHours')}
            </div>
          </motion.div>
        </div>

        {/* FAQ Section */}
        <motion.div
          className="faq-section"
          initial={hydrated ? { opacity: 0, y: 30 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2>{t('contact.faq')}</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${openFaq === index ? 'open' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  {openFaq === index ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>Contact AstroEpanchang - Get in Touch with Us</h2>
          <p>Have questions about Vedic astrology, birth charts, horoscope readings, or our services? Reach out to us through the contact form above. Our team is available to assist you with technical support, feedback, and general inquiries about AstroEpanchang's astrology tools and features.</p>
          <p>AstroEpanchang is your trusted platform for authentic Vedic astrology services including free Kundli generation, daily horoscopes, marriage matching, Panchang, Hindu festival calendars, and muhurtham dates. We are committed to providing accurate astrological insights based on traditional Vedic principles.</p>

          <h3>About AstroEpanchang</h3>
          <p>AstroEpanchang is a comprehensive Vedic astrology platform dedicated to making traditional astrological wisdom accessible to everyone. Founded on the principles of authentic Jyotish (Vedic astrology), we provide scientifically calculated, Panchang-based astrological services for individuals, families, and astrology enthusiasts across India and worldwide. Our platform uses Swiss Ephemeris calculations with Lahiri Ayanamsa for the highest accuracy in planetary positions and predictions.</p>

          <h3>Our Astrology Services</h3>
          <p><strong>Free Kundli (Birth Chart) Generator:</strong> Generate your complete Vedic birth chart with Rasi chart, Navamsa chart, planetary positions, Nakshatra details, Vimshottari Dasha, Shadbala, and Manglik Dosha analysis — all completely free.</p>
          <p><strong>Daily Horoscope Predictions:</strong> Read accurate daily, weekly, and monthly horoscope predictions for all 12 zodiac signs based on real-time planetary transits. Our predictions cover career, love, health, and finances.</p>
          <p><strong>Marriage Compatibility (Kundli Matching):</strong> Check horoscope compatibility using the traditional Tamil 12 Porutham system and North Indian Ashtakoota system. Get detailed compatibility scores and expert guidance for marriage decisions.</p>
          <p><strong>Panchangam (Hindu Calendar):</strong> Access daily Panchang with Tithi, Nakshatra, Yoga, Karana, Vara, Rahu Kalam, Gulikai, Yamaganda, Abhijit Muhurta, and Chandrashtama — accurate for your location.</p>
          <p><strong>Hindu Festival Calendar:</strong> Complete year-round Hindu festival dates including Navratri, Diwali, Maha Shivaratri, Ganesh Chaturthi, Pongal, and hundreds more regional festivals with their significance and rituals.</p>
          <p><strong>Tamil Muhurtham Dates:</strong> Find auspicious dates for weddings, griha pravesh, and important life events with our Muhurtham calendar based on Tamil Panchangam calculations.</p>

          <h3>Frequently Asked Questions About Our Services</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>Are AstroEpanchang's services completely free?</h4>
              <p>Yes, our core services — Kundli generation, daily horoscopes, marriage compatibility check, Panchang, festival calendar, and muhurtham dates — are completely free to use. No registration is required for basic features. Premium personalized consultation services with certified astrologers are available at affordable rates.</p>
            </div>
            <div className="faq-seo-item">
              <h4>How accurate are AstroEpanchang's calculations?</h4>
              <p>We use Swiss Ephemeris — the gold standard in astronomical calculations used by professional astrologers worldwide — with Lahiri Ayanamsa (the official ayanamsa of the Indian government). Our Panchang calculations are verified against traditional almanacs for maximum accuracy. Planetary positions are accurate to within seconds of arc.</p>
            </div>
            <div className="faq-seo-item">
              <h4>How quickly will I receive a response to my inquiry?</h4>
              <p>We aim to respond to all general inquiries within 24-48 hours. Technical support requests are typically addressed within 12 hours during business hours. Premium members have access to priority support with faster response times.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Can I get a personalized astrological consultation?</h4>
              <p>Yes, AstroEpanchang connects you with experienced, certified Vedic astrologers for personalized consultations on birth chart reading, marriage compatibility, career astrology, Muhurtham selection, and remedial measures (Parihara). Visit our Consultation page to browse available astrologers and book a session.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
